import { ProductTitle, SelectedVariantPrice } from "@shopify/hydrogen";
import { ProductProvider, useCart } from "@shopify/hydrogen/client";
import type { Product } from "@shopify/hydrogen/dist/esnext/graphql/types/types";
import { useEffect } from "react";

import { useCartUI } from "../../hooks/use-cart-ui";
import { usePrevious } from "../../hooks/use-previous";
import type { CheckoutEnabledConfig } from "../../lookbook";
import { ItemDetails } from "../item/ItemDetails";
import { Modal, ModalSection } from "../Modal";
import { ProductOptions } from "./ProductOptions";
import { SelectedVariantButtons } from "./SelectedVariantButtons";

export interface AddToBagOrBuyNowModalProps {
  checkout: CheckoutEnabledConfig;
  onUnpause: () => void;
}

export function AddToBagOrBuyNowModal(props: AddToBagOrBuyNowModalProps) {
  const { checkout, onUnpause } = props;

  const { itemModalState, closeItemModal } = useCartUI();

  const { status: currentStatus } = useCart();

  const prevStatus = usePrevious(currentStatus);

  useEffect(() => {
    if (
      (prevStatus === "creating" && currentStatus === "idle") ||
      (prevStatus === "updating" && currentStatus === "idle")
    ) {
      closeItemModal();
      onUnpause();
    }
  }, [closeItemModal, currentStatus, onUnpause, prevStatus]);

  if (!itemModalState.product) {
    return null;
  }

  return (
    <ProductProvider
      product={itemModalState.product as Product}
      initialVariantId={itemModalState.initialVariantId}>
      <Modal
        id="product-options-modal"
        testId="product-options-modal"
        active={itemModalState.open}
        header={
          <ItemDetails
            clamped={false}
            showPrice={true}
            price={<SelectedVariantPrice />}
            showTitle={true}
            title={<ProductTitle />}
            showVendor={false}
            vendor={null}
            showComments={false}
            comments={null}
          />
        }
        footer={<SelectedVariantButtons />}
        onClose={() => {
          closeItemModal();
          onUnpause();
        }}>
        <ModalSection id="product-options-modal-section" title="Add to bag">
          <div className="space-y-8">
            <ProductOptions checkout={checkout} />
          </div>
        </ModalSection>
      </Modal>
    </ProductProvider>
  );
}
